import { PaginatedResponse } from "src/api/types";
import { z } from "zod";
import { getCsrfToken } from "./auth-client";
import { validateRequest, validateResponse } from "./validation/middleware";

interface APIClientConfig {
  baseURL?: string;
  transformResolvedUrl?: (url: string) => string;
}

interface ValidatedRequestConfig<
  TReq extends z.ZodType,
  TRes extends z.ZodType,
> extends RequestInit {
  validation?: {
    request?: TReq;
    response?: TRes;
  };
}

export class APIClient {
  private baseURL: string;
  private transformResolvedUrl: (url: string) => string;

  constructor(config?: APIClientConfig) {
    this.baseURL = config?.baseURL || process.env.NEXT_PUBLIC_API_URL || "/";
    this.transformResolvedUrl = config?.transformResolvedUrl ?? ((url) => url);
  }

  private async getAuthToken(): Promise<string | null> {
    // Browser requests should rely on cookies; only server-side code forwards auth tokens.
    if (typeof window !== "undefined") {
      return null;
    }

    // Check if we're in a Node.js environment before trying to require
    if (typeof require === "undefined") {
      return null;
    }

    // Handle token retrieval for server-side
    try {
      const { cookies } = require("next/headers");
      const cookieStore = await cookies();
      return cookieStore.get("auth_token")?.value ?? null;
    } catch (error) {
      // If require fails (e.g., in Storybook or other environments), return null
      return null;
    }
  }

  private async prepareRequest<TReq extends z.ZodType = any>(
    endpoint: string,
    init?: ValidatedRequestConfig<TReq, any>,
  ): Promise<{ endpoint: string; response: Response }> {
    const url = this.resolveUrl(endpoint);
    const token = await this.getAuthToken(); // for authorizing server-side requests
    const csrfToken = getCsrfToken(); // for validating client-side requests
    const isFormData = init?.body instanceof FormData;

    // Validate request body if schema provided
    let validatedBody = init?.body;
    if (init?.body && init.validation?.request) {
      const requestData = JSON.parse(init.body as string);
      const validatedData = await validateRequest(endpoint, init.validation.request, requestData);
      validatedBody = JSON.stringify(validatedData);
    }

    const headers = new Headers(init?.headers);
    if (!isFormData && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
    if (csrfToken) {
      headers.set("X-CSRFToken", csrfToken);
    }
    if (token) {
      headers.set("Authorization", `Token ${token}`);
    }

    // Convert Headers object to plain object for fetch compatibility
    const headersObject: Record<string, string> = {};
    headers.forEach((value, key) => {
      headersObject[key] = value;
    });

    const response = await fetch(url, {
      ...init,
      body: validatedBody,
      headers: headersObject,
      credentials: "include",
    });

    return { endpoint, response };
  }

  async fetchRaw<TReq extends z.ZodType = any>(
    endpoint: string,
    init?: ValidatedRequestConfig<TReq, any>,
  ): Promise<Response> {
    const { response } = await this.prepareRequest(endpoint, init);
    return response;
  }

  async fetch<T = any, TReq extends z.ZodType = any, TRes extends z.ZodType = any>(
    endpoint: string,
    init?: ValidatedRequestConfig<TReq, TRes>,
  ): Promise<T> {
    try {
      const { endpoint: validatedEndpoint, response } = await this.prepareRequest(endpoint, init);

      if (!response || response.status === 401) {
        throw new Error("Unauthorized");
      }

      if (response.status < 200 || response.status >= 300) {
        const cause = response.headers.get("content-type")?.includes("application/json")
          ? await response.json()
          : { message: response.statusText };
        const error = new Error(`API error: ${response.statusText}`, { cause });
        // Attach status code to error for better error handling
        (error as any).status = response.status;
        throw error;
      }

      if (response.status === 204) {
        return null as T;
      }

      const responseData = await response.json();

      // Validate response if schema provided
      if (init?.validation?.response) {
        return validateResponse(validatedEndpoint, init.validation.response, responseData) as T;
      }

      return responseData;
    } catch (error) {
      throw error;
    }
  }

  async fetchAllPages<T = any>(
    endpoint: string,
    options?: {
      isParallel?: boolean;
      batchSize?: number;
      pageSize?: number;
    },
  ): Promise<T[]> {
    const { isParallel: parallel = true, batchSize = 10, pageSize = 200 } = options || {};

    // Add limit if not present
    let endpointWithLimit = endpoint;
    if (!endpoint.includes("limit=")) {
      const separator = endpoint.includes("?") ? "&" : "?";
      endpointWithLimit = `${endpoint}${separator}limit=${pageSize}`;
    }

    // Fetch the first page
    const firstResponse = await this.fetch<PaginatedResponse<T> | T[]>(endpointWithLimit);

    // If response is an array or doesn't have a count field, return it as-is
    if (Array.isArray(firstResponse) || !("count" in firstResponse)) {
      return firstResponse as T[];
    }

    // Handle paginated response
    const paginatedResponse = firstResponse as PaginatedResponse<T>;
    let allResults: T[] = [...paginatedResponse.results];

    // If no more pages, return the results
    if (!paginatedResponse.next || paginatedResponse.count <= paginatedResponse.results.length) {
      return allResults;
    }

    if (parallel) {
      // Parallel fetching logic
      const totalCount = paginatedResponse.count;
      const pageSize = paginatedResponse.results.length;
      const totalPages = Math.ceil(totalCount / pageSize);

      // Generate URLs for all remaining pages
      const pageUrls: string[] = [];
      const hasQueryParams = endpointWithLimit.includes("?");

      for (let page = 2; page <= totalPages; page++) {
        const offset = (page - 1) * pageSize;
        const separator = hasQueryParams ? "&" : "?";
        pageUrls.push(`${endpointWithLimit}${separator}offset=${offset}`);
      }

      // Fetch pages in batches
      for (let i = 0; i < pageUrls.length; i += batchSize) {
        const batch = pageUrls.slice(i, i + batchSize);
        const batchPromises = batch.map((url) => this.fetch<PaginatedResponse<T>>(url));

        const batchResponses = await Promise.all(batchPromises);
        for (const response of batchResponses) {
          allResults = [...allResults, ...response.results];
        }
      }
    } else {
      // Sequential fetching logic (fallback)
      let nextUrl: string | null = paginatedResponse.next;

      while (nextUrl) {
        const response: PaginatedResponse<T> = await this.fetch<PaginatedResponse<T>>(nextUrl);
        allResults = [...allResults, ...response.results];
        nextUrl = response.next;
      }
    }

    return allResults;
  }

  private resolveUrl(endpoint: string): string {
    if (/^https?:\/\//i.test(endpoint)) {
      return this.transformResolvedUrl(endpoint);
    }

    const resolvedUrl = endpoint.startsWith(this.baseURL) ? endpoint : `${this.baseURL}${endpoint}`;
    return this.transformResolvedUrl(resolvedUrl);
  }
}
